import { useEffect, useRef, useState } from 'react';
import { X, ShieldCheck, Check, RotateCcw, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { EntitledFramework, FrameworkStatus } from '@/api/types/cts-frameworks.types';

/**
 * MANAGE FRAMEWORKS — the tenant's framework discovery and workspace management surface (TC3).
 *
 * ⚠ EVERY FRAMEWORK THE TENANT IS ENTITLED TO, IN ONE PLACE, WITH ITS CURRENT STATE NAMED. The
 * main page is for working; this window is for deciding. Keeping the three states visible together
 * is what makes "removed" a place you can come back to rather than a thing that disappeared.
 *
 * ⚠ NO ACTION HERE COMMITS DIRECTLY. Adding and removing both route through a confirmation,
 * because both change what the whole organisation's compliance programme is measured against —
 * and a workspace altered by a mis-click is a workspace the tenant has to reason about later
 * without knowing it changed.
 */

const STATE_LABEL: Record<FrameworkStatus, string> = {
  adopted: 'In Workspace',
  available: 'Available to Add',
  removed: 'Removed',
};
const STATE_TONE: Record<FrameworkStatus, string> = {
  adopted: 'bg-cert-valid-fg/12 text-cert-valid-fg',
  available: 'bg-bg-surface-2 text-text-secondary',
  removed: 'bg-bg-surface-2 text-text-faint',
};

export function ManageFrameworksModal({
  frameworks, onClose, onAdd, onRemove, busy,
}: {
  frameworks: EntitledFramework[];
  onClose: () => void;
  onAdd: (f: EntitledFramework) => void;
  onRemove: (f: EntitledFramework) => void;
  busy: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    ref.current?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const needle = filter.trim().toLowerCase();
  const shown = needle
    ? frameworks.filter((f) => `${f.name} ${f.publisher ?? ''} ${f.display_code}`.toLowerCase().includes(needle))
    : frameworks;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/55 p-[20px] pt-[7vh]"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        ref={ref} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby="manage-fw-title"
        className="flex max-h-[80vh] w-full max-w-[720px] flex-col rounded-[12px] border border-cert-neutral-border bg-bg-base shadow-2xl outline-none"
      >
        <div className="flex items-start gap-[12px] border-b border-border-subtle px-[20px] py-[16px]">
          <div className="flex-1">
            <h2 id="manage-fw-title" className="text-[16px] font-semibold text-text-primary">Manage Frameworks</h2>
            <p className="mt-[3px] text-[12.5px] text-text-secondary">
              Every framework licensed to your organisation. Add the ones you are working, and
              remove the ones you are not — your work is kept either way.
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close"
            className="text-text-faint transition-colors hover:text-text-primary">
            <X className="h-[16px] w-[16px]" aria-hidden="true" />
          </button>
        </div>

        {frameworks.length > 4 && (
          <div className="border-b border-border-subtle px-[20px] py-[10px]">
            <input
              type="search" value={filter} onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter frameworks" aria-label="Filter frameworks"
              className="w-full rounded-[8px] border border-cert-neutral-border bg-bg-surface px-[11px] py-[7px] text-[13px] text-text-primary placeholder:text-text-faint focus:border-accent-cyan focus:outline-none"
            />
          </div>
        )}

        <div className="min-h-0 flex-1 overflow-y-auto">
          {shown.length === 0 ? (
            <p className="px-[20px] py-[24px] text-[13px] italic text-text-faint">
              {frameworks.length === 0
                ? 'No frameworks are licensed to your organisation yet.'
                : 'No frameworks match that filter.'}
            </p>
          ) : shown.map((f) => (
            <div key={f.reference_id}
              className="flex items-start gap-[13px] border-b border-border-subtle px-[20px] py-[14px] last:border-b-0">
              <span className="mt-[1px] flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-[8px] bg-bg-surface-2">
                <ShieldCheck className="h-[15px] w-[15px] text-accent-cyan" strokeWidth={1.5} aria-hidden="true" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-[8px]">
                  <span className="text-[13.5px] font-medium text-text-primary">{f.name}</span>
                  <span className={cn('rounded-[4px] px-[7px] py-[1px] text-[10px] font-medium', STATE_TONE[f.status])}>
                    {STATE_LABEL[f.status]}
                  </span>
                </div>
                <p className="mt-[2px] text-[11px] text-text-secondary">
                  {[f.publisher, f.version_label].filter(Boolean).join(' · ') || f.display_code}
                </p>
                {f.description && (
                  <p className="mt-[5px] max-w-[62ch] text-[12px] leading-[1.5] text-text-secondary">
                    {f.description}
                  </p>
                )}
                {/*
                  ⚠ A removed framework says its work is intact, HERE, where the decision to restore
                  is made. Putting that only in a toast at removal time means the tenant reading
                  this list weeks later has no way to know whether restoring recovers anything.
                */}
                {f.status === 'removed' && (
                  <p className="mt-[5px] text-[11.5px] text-text-faint">
                    Removed from your workspace. Your control work, evidence and assessments were
                    kept and return if you add it back.
                  </p>
                )}
              </div>
              <div className="shrink-0 pt-[2px]">
                {f.status === 'adopted' ? (
                  <button type="button" onClick={() => onRemove(f)} disabled={busy}
                    className="rounded-[7px] border border-cert-neutral-border px-[11px] py-[6px] text-[12px] text-text-secondary transition-colors hover:text-text-primary disabled:opacity-50">
                    Remove
                  </button>
                ) : (
                  <button type="button" onClick={() => onAdd(f)} disabled={busy}
                    className="flex items-center gap-[5px] rounded-[7px] bg-accent-cyan px-[11px] py-[6px] text-[12px] font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50">
                    {f.status === 'removed'
                      ? <><RotateCcw className="h-[12px] w-[12px]" aria-hidden="true" /> Restore</>
                      : <><Plus className="h-[12px] w-[12px]" aria-hidden="true" /> Add</>}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-[10px] border-t border-border-subtle px-[20px] py-[12px]">
          <Check className="h-[13px] w-[13px] shrink-0 text-text-faint" aria-hidden="true" />
          <p className="flex-1 text-[11.5px] text-text-secondary">
            Removing a framework never affects your licence or deletes any work.
          </p>
          <button type="button" onClick={onClose}
            className="rounded-[7px] border border-cert-neutral-border px-[12px] py-[6px] text-[12px] text-text-secondary transition-colors hover:text-text-primary">
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
